import { IBase } from "./Base";
import { WorkflowJob } from "./WorkflowJob";

export type Workflow = IBase & {
  name: string;
  description?: string;
  group?: string | null;
  enabled: boolean;
  editor_state: string;
  steps: WorkflowStep[];
  jobs: WorkflowJob[];
  triggers: WorkflowTrigger[];
};

export type WorkflowTrigger = IBase & {
  workflow_id: number;
  type: WorkflowTriggerType;
  enabled: boolean;
  config: WorkflowTriggerConfig;
};

export type WorkflowTriggerConfig = {
  event?: WorkflowEvent;
  event_entity?: string;
  manual_entities?: string[];
  cron_expression?: string;
  last_run_at?: string | null;
  next_run_at?: string | null;
  api_route?: string;
  filter?: FilterExpression | null;
};

export type WorkflowStep = IBase & {
  key: string;
  name: string;
  description?: string;
  enabled: boolean;
  action: WorkflowAction;
  parameters: {
    [key: string]:
      | string
      | number
      | boolean
      | null
      | FilterCondition[]
      | Record<string, unknown>;
  };
  is_start_step: boolean;
  on_success_step_id: number | null;
  on_failure_step_id: number | null;
};

export type CreateUpdateWorkflowTrigger = {
  type: WorkflowTriggerType;
  enabled: boolean;
  config: WorkflowTriggerConfig;
};

export type FilterOp =
  | "eq" | "neq"
  | "is_null" | "is_not_null"
  | "gt" | "gte" | "lt" | "lte"
  | "in"
  | "contains"
  | "changed" | "changed_to";

export interface FilterExpression {
  and?: FilterExpression[];
  or?: FilterExpression[];
  not?: FilterExpression;
  field?: string;
  op?: FilterOp;
  value?: unknown;
}

export type CreateUpdateWorkflow = Pick<
  Workflow,
  "name" | "description" | "group" | "enabled" | "editor_state"
> & {
  triggers: CreateUpdateWorkflowTrigger[];
};

export type UpdateWorkflowStep = Pick<
  WorkflowStep,
  | "name"
  | "description"
  | "enabled"
  | "action"
  | "parameters"
  | "is_start_step"
  | "on_success_step_id"
  | "on_failure_step_id"
>;

export type CreateUpdateWorkflowStep = UpdateWorkflowStep & {
  key: string;
};

export enum WorkflowTriggerType {
  None = "",
  Api = "Api",
  Event = "Event",
  Timed = "Timed",
  Manual = "Manual",
}

export enum WorkflowEvent {
  None = "",
  EntityCreated = "EntityCreated",
  EntityUpdated = "EntityUpdated",
  EntityDeleted = "EntityDeleted",
  UserRegistered = "UserRegistered",
  UserInvited = "UserInvited",
  SubscriptionCreated = "SubscriptionCreated",
  SubscriptionActivated = "SubscriptionActivated",
  SubscriptionExpired = "SubscriptionExpired",
  SubscriptionCancelled = "SubscriptionCancelled",
  PaymentSucceeded = "PaymentSucceeded",
  PaymentFailed = "PaymentFailed",
  PaymentMethodCaptured = "PaymentMethodCaptured",
  PaymentMethodCaptureFailed = "PaymentMethodCaptureFailed",
  PaymentMethodRemoved = "PaymentMethodRemoved",
  PushActionTaken = "PushActionTaken",
}

export enum WorkflowAction {
  None = "",
  ReadData = "ReadData",
  CreateData = "CreateData",
  UpdateData = "UpdateData",
  DeleteData = "DeleteData",
  CallAnApi = "CallAnApi",
  RunScript = "RunScript",
  SendACommand = "SendACommand",
  Condition = "Condition",
  SendAnEmail = "SendAnEmail",
  Integration = "Integration",
  FileHandler = "FileHandler",
  UpsertData = "UpsertData",
  SendPushNotification = "SendPushNotification",
  AdjustBalance = "AdjustBalance",
}

export enum WorkflowDataSource {
  None = "None",
  Trigger = "Trigger",
  Step = "Step",
}

export enum WorkflowDataIterationMode {
  All = "All",
  First = "First",
  Last = "Last",
  Nth = "Nth",
}

export enum WorkflowStatus {
  Pending = "Pending",
  Running = "Running",
  Success = "Success",
  Failed = "Failed",
  Unknown = "Unknown",
}

// Define filter condition type
export interface FilterCondition {
  field: string;
  operator: string;
  value: string;
}

export interface EditorState {
  nodePositions: Record<string, { x: number; y: number }>;
  viewport: { x: number; y: number; zoom: number };
  version: number;
  lastSaved: string;
}

export interface TriggerWorkflowRequest {
  entity_name?: string;
  entity_id?: number;
  data?: string;
}
